import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@agms/ui'
import { PageHeader, SurfaceCard } from '../components/ui'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import api from '../lib/api'
import { formatCurrencyWithCentsOrDash } from '../lib/format'

interface AnalysisSummary {
  id: string
  merchant_label: string
  statement_period: string | null
  pricing_model: string
  analyzed_at: string | null
  lead?: { id: string; dba_name: string | null; legal_name: string | null } | null
  result: { merchant_savings?: string | null; agms_margin?: string | null } | null
}

interface RepReportRow {
  user_id: string | null
  name: string
  role: string | null
  leads: number
  won: number
  lost: number
  open: number
  win_rate: string | null
  expected_monthly_residual: string
}

export function StatementAnalysisPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [merchantLabel, setMerchantLabel] = useState('')
  const [error, setError] = useState<string | null>(null)

  const analyses = useQuery({
    queryKey: ['statement-analyses'],
    queryFn: async () => {
      const response = await api.get<{ data: AnalysisSummary[] }>('/statement-analyses')
      return response.data.data
    },
  })

  const report = useQuery({
    queryKey: ['statement-analysis-rep-report'],
    queryFn: async () => {
      const response = await api.get<{ data: RepReportRow[] }>('/statement-analyses/rep-report')
      return response.data.data
    },
  })

  const create = useMutation({
    mutationFn: async () => {
      const response = await api.post<{ id: string }>('/statement-analyses', {
        merchant_label: merchantLabel,
      })
      return response.data
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['statement-analyses'] })
      navigate(`/statement-analysis/${created.id}`)
    },
    onError: (caught) => setError(apiErrorMessage(caught, 'The analysis could not be created.')),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Statement Analysis"
        subtitle="Compare a processing statement with a proposed price and Schedule A."
      />

      <SurfaceCard>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            create.mutate()
          }}
        >
          <Input
            label="Merchant label"
            value={merchantLabel}
            onChange={(event) => setMerchantLabel(event.target.value)}
            required
            maxLength={255}
          />
          <Button type="submit" disabled={create.isPending}>
            New analysis
          </Button>
        </form>
        {error ? <p className="mt-3 text-sm text-red-700" role="alert">{error}</p> : null}
      </SurfaceCard>

      <SurfaceCard>
        {analyses.isLoading ? <p className="text-sm text-gray-500">Loading analyses…</p> : null}
        {analyses.isError ? (
          <p className="text-sm text-red-700" role="alert">Analyses could not be loaded.</p>
        ) : null}
        {analyses.data && analyses.data.length === 0 ? (
          <p className="text-sm text-gray-500">No statement analyses yet.</p>
        ) : null}
        <ul className="divide-y divide-gray-100">
          {analyses.data?.map((analysis) => (
            <li key={analysis.id} className="py-3">
              <Link to={`/statement-analysis/${analysis.id}`} className="font-medium text-sky-700 hover:text-sky-800">
                {analysis.merchant_label}
              </Link>
              {analysis.lead ? (
                <p className="text-xs text-gray-600">
                  Lead{' '}
                  <Link to={`/leads/${analysis.lead.id}`} className="text-sky-700 hover:text-sky-800">
                    {analysis.lead.dba_name || analysis.lead.legal_name || 'Unnamed lead'}
                  </Link>
                </p>
              ) : null}
              <p className="text-xs text-gray-500">
                {analysis.statement_period || 'No statement period'}
                {' · '}
                Savings {formatCurrencyWithCentsOrDash(analysis.result?.merchant_savings)}
                {' · '}
                Margin {formatCurrencyWithCentsOrDash(analysis.result?.agms_margin)}
              </p>
            </li>
          ))}
        </ul>
      </SurfaceCard>

      <SurfaceCard>
        <h2 className="text-sm font-semibold text-gray-900">Win rate and expected residual</h2>
        <p className="mt-1 text-xs text-gray-500">
          Win rate is converted leads divided by converted plus declined leads. Open leads stay out of the rate.
          Expected residual is the statement-period AGMS margin for leads that are not declined.
        </p>
        {report.isLoading ? <p className="mt-3 text-sm text-gray-500">Loading report…</p> : null}
        {report.isError ? (
          <p className="mt-3 text-sm text-red-700" role="alert">The report could not be loaded.</p>
        ) : null}
        {report.data && report.data.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No analyses yet.</p>
        ) : null}
        {report.data && report.data.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">Win rate and expected residual by rep</caption>
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th scope="col" className="py-2 pr-4 font-medium">Rep</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Leads</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Won</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Lost</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Open</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Win rate</th>
                  <th scope="col" className="py-2 font-medium">Expected residual</th>
                </tr>
              </thead>
              <tbody>
                {report.data.map((row) => (
                  <tr key={row.user_id ?? row.name} className="border-b border-gray-100">
                    <th scope="row" className="py-2 pr-4 font-medium text-gray-900">{row.name}</th>
                    <td className="py-2 pr-4">{row.leads}</td>
                    <td className="py-2 pr-4">{row.won}</td>
                    <td className="py-2 pr-4">{row.lost}</td>
                    <td className="py-2 pr-4">{row.open}</td>
                    <td className="py-2 pr-4">{row.win_rate === null ? '—' : `${row.win_rate}%`}</td>
                    <td className="py-2">{formatCurrencyWithCentsOrDash(row.expected_monthly_residual)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </SurfaceCard>
    </div>
  )
}
